<template>
  <v-card style="overflow-y: hidden;">
    <v-container>
      <v-row>
        <v-col cols="10" offset="1">
          <v-form ref="form">
            <v-container>
              <v-row>
                <v-col align="center">
                  <PageTitle :title="label('title')" />
                </v-col>
              </v-row>
              <v-row>
                <JobForm
                  ref="jobForm"
                  v-model="data"
                  :sideValues="sideValues"
                  :type="type"
                  @inputSide="updateSide"
                />
              </v-row>
              <v-row v-if="completedMessage">
                <MessageBox type="success" :message="completedMessage" />
              </v-row>
              <v-row v-if="errorMessage">
                <ErrorMessage :message="errorMessage" />
              </v-row>
              <v-row>
                <v-col cols="4">
                  <ActionButton type="save" @click="saveToPublish">
                    {{ label("publish") }}
                  </ActionButton>
                </v-col>
                <v-col cols="4">
                  <ActionButton type="cancel" @click="saveToDraft">
                    {{ label("draft") }}
                  </ActionButton>
                </v-col>
                <v-col cols="4">
                  <ActionButton type="delete" @click="cancel">
                    {{ label("cancel") }}
                  </ActionButton>
                </v-col>
              </v-row>
              <v-row justify="center">
                <a
                  href="https://info.nishika.com/connect/employment-security-act"
                  target="_blank"
                  style="text-decoration: underline; font-size: 0.8rem;"
                  >各種法律の趣旨に則った情報記載のお願い</a
                >
              </v-row>
            </v-container>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
    <MessageDialog :open="openPublishMessage" @ok="onClosePublishMessage">
      <p>公開申請がされました。</p>
      <p>{{ publishMessage }}</p>
    </MessageDialog>
    <MessageDialog :open="openSaveMessage" @ok="onCloseSaveMessage">
      <p>求人が一時保存されました。</p>
      <p>この求人を公開したい場合は、公開申請を行ってください。</p>
    </MessageDialog>
  </v-card>
</template>

<script>
import PageTitle from "../../../atoms/PageTitle";
import JobForm from "../../../organisms/JobForm";
import ActionButton from "../../../molecules/ActionButton";
import ErrorMessage from "../../../molecules/ErrorMessage";
import ComponentMixin from "../../../ComponentMixin";
import { mapGetters } from "vuex";
import MessageBox from "../../../atoms/MessageBox";
import ApiMixin from "../../../../ApiMixin";
import _clone from "lodash/clone";
import MessageDialog from "@/components/molecules/MessageDialog";

export default {
  mixins: [ComponentMixin, ApiMixin],
  type: "page",
  name: "ClientJobEditPage",
  isRequiredLogin: true,
  data() {
    return {
      data: {},
      sideValues: null,
      completedMessage: null,
      errorMessage: null,
      openPublishMessage: false,
      openSaveMessage: false,
    };
  },
  props: {
    hideCancel: {
      type: Boolean,
    },
    open: {
      type: Boolean,
    },
  },
  beforeMount() {
    this.init();
  },
  methods: {
    async init() {
      if (this.user.id) {
        const jobId = this.$route.params.id;
        await this.loadClientJob(jobId);
        this.$refs.jobForm.resetSideJobErrors();
      }
      this.completedMessage = null;
      this.errorMessage = null;
    },
    async saveToPublish(e, guard) {
      if (this.data.preference.proxyStatus) {
        this.$refs.jobForm.changePanel(0)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      if (this.$refs.form.validate() && this.$refs.jobForm.validate()) {
        guard((release) => {
          this.data.approvalStatus = 1;
          delete this.data.id;
          delete this.data.company;
          if (this.side) {
            this.data.side = this.sideValues;
          }
          this.saveJob(this.$route.params.id, this.data)
            .then((res) => {
              if (this.data.image) {
                this.saveJobImage(res.data.id, this.data.image)
                  .then(() => {
                    this.openPublishMessage = true;
                  })
                  .catch(() => {
                    this.errorMessage = this.label("failedUpdateData");
                  })
                  .finally(release);
              } else {
                release();
                this.gtmDataLayer({
                  event: "publish_jobs",
                  service: "connect-clients",
                });
                this.openPublishMessage = true;
              }
            })
            .catch(() => {
              release();
              this.errorMessage = this.label("failedUpdateData");
            });
        }, false);
      } else {
        const childRefs = this.$refs.jobForm.$refs;
        const jobFormInputs = this.side
          ? childRefs.hourlyWageForm.inputs.concat(childRefs.workingHoursWeekForm.inputs)
          : childRefs.salaryForm.inputs;
        const inputs = this.$refs.form.inputs.concat(jobFormInputs);
        const firtstError = inputs.find((input) => {
          return input.hasError;
        });
        if (firtstError) {
          this.$vuetify.goTo(firtstError, { offset: 100 });
        }
      }
    },
    saveToDraft(e, guard) {
      guard((release) => {
        this.data.approvalStatus = 0;
        delete this.data.id;
        delete this.data.company;
        if (this.side) {
          this.data.side = this.sideValues;
        }
        this.saveJob(this.$route.params.id, this.data)
          .then((res) => {
            if (this.data.image) {
              this.saveJobImage(res.data.id, this.data.image)
                .then(() => {
                  this.openSaveMessage = true;
                })
                .catch(() => {
                  this.errorMessage = this.label("failedUpdateData");
                })
                .finally(release);
            } else {
              release();
              this.openSaveMessage = true;
            }
          })
          .catch(() => {
            release();
            this.errorMessage = this.label("failedUpdateData");
          });
      }, false);
    },
    cancel() {
      this.init();
      this.go(`/clients/jobs`);
    },
    onClosePublishMessage() {
      this.openPublishMessage = false;
      this.go("/clients/jobs");
    },
    onCloseSaveMessage() {
      this.openSaveMessage = false;
      this.go("/clients/jobs");
    },
    updateSide(value) {
      this.sideValues = value;
    },
  },
  computed: {
    ...mapGetters("client", ["company", "job"]),
    ...mapGetters("user", ["user"]),
    publishMessage() {
      if (this.job.approvalStatus === 0) {
        return "申請が承認され次第、求人内容が公開されます。";
      }
      return "申請が承認され次第、公開されている求人内容が更新されます。";
    },
    type() {
      if (this.data.side == null) {
        return "regular";
      }
      return "side";
    },
    side() {
      return this.type === "side";
    },
  },
  components: {
    MessageDialog,
    PageTitle,
    ErrorMessage,
    JobForm,
    ActionButton,
    MessageBox,
  },
  watch: {
    "user.id": {
      handler: function() {
        this.init();
      },
      immediate: true,
    },
    job: {
      handler: function(value) {
        this.data = _clone(value);
        this.data.resourceType = value.resourceType || 3;
        this.data.developmentType = value.developmentType || 3;
        this.data.companyScale = value.companyScale || 3;
        this.data.workingStance = value.workingStance || 3;
        this.data.preference = value.preference || { proxyStatus: false };
        if (this.side) {
          this.sideValues = this.data.side;
        }
      },
    },
  },
};
</script>
